import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { CONTAINER, EYEBROW, NAMESPACE, PauseChip, Rich, SECTION_LEAD, SECTION_TITLE } from '../components/ui';
import type { SimHandle } from '../engine/hero-sim';
import { initWinsSim } from '../engine/wins-sim';
import { buildWinsStrings } from '../strings';

const CARD =
  'win relative grid content-start gap-4 overflow-hidden rounded-[1.5rem] border border-white/10 bg-[radial-gradient(120%_90%_at_100%_0%,rgb(147_255_24/.07),transparent_55%),linear-gradient(180deg,rgb(255_255_255/.055),rgb(255_255_255/.012))] p-[clamp(1.25rem,.9rem+1.2vw,1.75rem)] shadow-[0_8px_24px_rgb(0_0_0/.45),0_2px_6px_rgb(0_0_0/.3),inset_0_1px_0_rgb(255_255_255/.06)] [transition:border-color_260ms_var(--w-ease),transform_320ms_var(--w-ease),box-shadow_320ms_var(--w-ease)] before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-[linear-gradient(90deg,transparent,rgb(147_255_24/.8),transparent)] before:opacity-55 forced-colors:border-[CanvasText] forced-colors:bg-[Canvas] motion-reduce:hover:[transform:none] [@media(pointer:fine)]:hover:border-[#93FF18]/35 [@media(pointer:fine)]:hover:shadow-[0_8px_24px_rgb(0_0_0/.45),0_2px_6px_rgb(0_0_0/.3),inset_0_1px_0_rgb(255_255_255/.06),0_0_60px_-20px_rgb(147_255_24/.35)] [@media(pointer:fine)]:hover:[transform:translateY(-4px)] [@media(width>=40rem)_and_(width<64rem)]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] [@media(width>=40rem)_and_(width<64rem)]:items-start [@media(width>=40rem)_and_(width<64rem)]:gap-x-[clamp(1.25rem,.8rem+1.6vw,2rem)] [@media(width>=64rem)]:grid-rows-[auto_auto_1fr]';
const CARD_TITLE = 'text-[clamp(1.375rem,1.1rem+1.1vw,1.75rem)] font-bold leading-[1.1] tracking-[-.028em] text-white break-words [@media(width<22rem)]:[overflow-wrap:anywhere] [&_em]:not-italic [&_em]:text-[#93FF18]';
const CARD_BODY = 'max-w-[32em] text-[.875rem] leading-[1.6] text-[#9AA3B5] [@media(width>=40rem)_and_(width<64rem)]:col-[1] [@media(width>=40rem)_and_(width<64rem)]:-mt-[.4rem]';
const VIZ = 'win__viz [@media(width>=40rem)_and_(width<64rem)]:col-[2] [@media(width>=40rem)_and_(width<64rem)]:row-[1/span_2] [@media(width>=64rem)]:self-stretch';
const VS_ROW = 'col-span-full grid grid-cols-subgrid items-center gap-x-[.6rem] [@media(width<19.5rem)]:gap-y-[.3rem]';

const FPS_BAD = ['34%', '28%', '40%', '24%', '36%', '22%', '38%', '30%'];
const FPS_GOOD = ['86%', '92%', '88%', '95%', '90%', '97%', '89%', '93%'];
const HOPS: [string, number, string?][] = [
  ['SAO', 4, 'is-best'],
  ['RIO', 6],
  ['BUE', 15],
  ['SCL', 20],
  ['MIA', 33, 'is-spike'],
];
const LINKS = ['fiber', 'wifi', 'fiber2', 'g5', 'g4', 'satellite'];

const cssVar = (name: string, value: string) => ({ [name]: value }) as CSSProperties;

interface WinCardProps {
  index: number;
  kicker: string;
  title: string;
  body: string;
  icon: ReactNode;
  titleBlockEm?: boolean;
  children: ReactNode;
}

function WinCard({ index, kicker, title, body, icon, titleBlockEm = false, children }: WinCardProps) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <article className={CARD} style={cssVar('--i', String(index))}>
      <span className='absolute end-[clamp(1.25rem,.9rem+1.2vw,1.75rem)] top-[clamp(1.25rem,.9rem+1.2vw,1.75rem)] font-mono text-[.75rem] tabular-nums tracking-[.08em] text-[#9AA3B5]' aria-hidden='true'>
        {String(index + 1).padStart(2, '0')}
      </span>
      <header className='grid gap-[.35rem] [@media(width>=40rem)_and_(width<64rem)]:col-[1]'>
        <span className='mb-[.35rem] grid size-11 place-items-center rounded-[.75rem] border border-[#93FF18]/[.28] bg-[#93FF18]/10 text-[#93FF18] shadow-[inset_0_0_22px_rgb(147_255_24/.16)]' aria-hidden='true'>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' focusable='false'>
            {icon}
          </svg>
        </span>
        <p className='text-[.6875rem] font-bold uppercase tracking-[.14em] text-[#9AA3B5]'>{t(kicker)}</p>
        <h3 className={classNames(CARD_TITLE, titleBlockEm && '[&_em]:block')}>
          <Rich i18nKey={title} />
        </h3>
      </header>
      <p className={CARD_BODY}>{t(body)}</p>
      <div className={VIZ}>{children}</div>
    </article>
  );
}

function VizLabel({ label }: { label: string }) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <p className='win__vk'>
      <span>{label}</span>
      <span className='simchip simchip--xs'>
        <i aria-hidden='true' />
        {t('common.simulation')}
      </span>
    </p>
  );
}

export function Wins() {
  const { t } = useTranslation(NAMESPACE);
  const sectionRef = useRef<HTMLElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const simRef = useRef<SimHandle | null>(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!sectionRef.current || !gridRef.current) return undefined;
    const sim = initWinsSim(sectionRef.current, gridRef.current, { paused, strings: buildWinsStrings(t) });
    simRef.current = sim;
    return () => {
      sim.destroy();
      simRef.current = null;
    };
    // the engine keeps its own clock; pause is handed over below instead of re-creating it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    simRef.current?.setPaused(paused);
  }, [paused]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) setPaused(true);
  }, []);

  return (
    <section ref={sectionRef} className='sec--wins relative isolate bg-[linear-gradient(180deg,rgb(255_255_255/.028),rgb(255_255_255/0)_30%,rgb(255_255_255/.022))] py-[clamp(3.5rem,2rem+5.5vw,7rem)] shadow-[inset_0_1px_0_rgb(255_255_255/.055),inset_0_-1px_0_rgb(255_255_255/.045)] [--w-ease:cubic-bezier(.22,.61,.36,1)] [--w-out:cubic-bezier(.16,1,.3,1)]' id='vitorias' aria-labelledby='wins-title'>
      <div className='pointer-events-none absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
        <span className='sec__grid' />
        <span className='sec__halo' />
      </div>
      <div className={CONTAINER}>
        <div className='relative mb-[clamp(2rem,1.2rem+2.6vw,3.25rem)] max-w-[46rem] [@media(width>=64rem)]:grid [@media(width>=64rem)]:max-w-none [@media(width>=64rem)]:grid-cols-[minmax(0,1fr)_auto] [@media(width>=64rem)]:items-end [@media(width>=64rem)]:gap-x-6' data-reveal>
          <p className={`${EYEBROW} [@media(width>=64rem)]:col-start-1 [@media(width>=64rem)]:row-start-1`}>{t('wins.eyebrow')}</p>
          <h2 className={`${SECTION_TITLE} [@media(width>=64rem)]:col-start-1 [@media(width>=64rem)]:row-start-2 [@media(width>=64rem)]:max-w-[20em]`} id='wins-title'>
            <Rich i18nKey='wins.title' />
          </h2>
          <p className={`${SECTION_LEAD} [@media(width>=64rem)]:col-start-1 [@media(width>=64rem)]:row-start-3`}>{t('wins.lead')}</p>
          <PauseChip className='mt-3.5 [@media(width>=64rem)]:col-start-2 [@media(width>=64rem)]:row-start-3 [@media(width>=64rem)]:-mt-1.5 [@media(width>=64rem)]:justify-self-end' paused={paused} onToggle={() => setPaused((previous) => !previous)} pauseAria={t('wins.pause_aria')} resumeAria={t('wins.resume_aria')} pauseLabel={t('common.pause')} resumeLabel={t('common.resume')} controls='wins-grid' />
        </div>

        <div ref={gridRef} className='wins grid gap-[clamp(1rem,.6rem+1.4vw,1.5rem)] [@media(width>=64rem)]:grid-cols-3' id='wins-grid' data-wins>
          <WinCard index={0} kicker='wins.ping.kicker' title='wins.ping.title' body='wins.ping.body' icon={<path d='M13 2.5 4.5 13.8h6L11 21.5l8.5-11.3h-6L13 2.5Z' />}>
            <VizLabel label={t('wins.ping.viz')} />
            <p className='sr-only'>{t('wins.ping.sr')}</p>
            <div className='vs grid grid-cols-[minmax(4.5rem,auto)_minmax(0,1fr)_calc(var(--text-xl)*1.85+1.5rem)] gap-[.55rem] [@media(width<19.5rem)]:grid-cols-[minmax(0,1fr)]' aria-hidden='true'>
              <div className={`vs__row vs__row--bad ${VS_ROW}`}>
                <span className='vs__lab'>{t('wins.ping.without')}</span>
                <span className='vs__bar'>
                  <i style={cssVar('--w', '85.6%')} data-sim-offbar />
                </span>
                <span className='vs__num tabular text-end'>
                  <b data-sim-off>214</b> ms
                </span>
              </div>
              <div className={`vs__row vs__row--good ${VS_ROW}`}>
                <span className='vs__lab'>{t('wins.ping.with')}</span>
                <span className='vs__bar'>
                  <i style={cssVar('--w', '1.6%')} data-sim-bar />
                </span>
                <span className='vs__num tabular text-end'>
                  <b data-sim-on>4</b> ms
                </span>
              </div>
            </div>
            <ul className='hops' aria-hidden='true'>
              {HOPS.map(([code, value, state]) => (
                <li key={code} className={state}>
                  {code}
                  <b className='tabular'>{value}</b>
                </li>
              ))}
            </ul>
            <p className='win__vf'>
              <span className='win__delta' data-sim-chip aria-hidden='true'>
                −98%
              </span>
              <span className='sr-only'>{t('wins.ping.foot_sr')}</span> {t('wins.ping.foot')}
            </p>
          </WinCard>

          <WinCard index={1} kicker='wins.fps.kicker' title='wins.fps.title' body='wins.fps.body' icon={<path d='M3.5 17.5V11M9 17.5V7M14.5 17.5v-4M20 17.5V4.5' />}>
            <VizLabel label={t('wins.fps.viz')} />
            <p className='sr-only'>{t('wins.fps.sr')}</p>
            <div className='fps' data-fps aria-hidden='true'>
              <div className='fps__side fps__side--bad'>
                <span className='fps__lab'>{t('wins.fps.without')}</span>
                <span className='fps__num tabular'>
                  <b data-fps-off>78</b>
                  <em>FPS</em>
                </span>
                <span className='fps__bars' aria-hidden='true'>
                  {FPS_BAD.map((height, index) => (
                    <i key={index} style={cssVar('--h', height)} />
                  ))}
                </span>
              </div>
              <div className='fps__side fps__side--good'>
                <span className='fps__lab'>{t('wins.fps.with')}</span>
                <span className='fps__num tabular'>
                  <b data-fps-on>163</b>
                  <em>FPS</em>
                </span>
                <span className='fps__bars' aria-hidden='true'>
                  {FPS_GOOD.map((height, index) => (
                    <i key={index} style={cssVar('--h', height)} />
                  ))}
                </span>
              </div>
            </div>
            <ul className='hops hops--tags' aria-hidden='true'>
              {(['cpu', 'gpu', 'ram'] as const).map((part) => (
                <li key={part} className='is-best'>
                  {part.toUpperCase()}
                  <b>{t(`wins.fps.${part}`)}</b>
                </li>
              ))}
            </ul>
            <p className='win__vf'>
              <span className='win__delta' data-fps-chip aria-hidden='true'>
                +109%
              </span>
              <span className='sr-only'>+109%</span> {t('wins.fps.foot')}
            </p>
          </WinCard>

          <WinCard
            index={2}
            kicker='wins.stability.kicker'
            title='wins.stability.title'
            body='wins.stability.body'
            titleBlockEm
            icon={
              <>
                <path d='M12 3.6 5 6.3v5.4c0 4.3 2.9 7.5 7 8.7 4.1-1.2 7-4.4 7-8.7V6.3l-7-2.7Z' />
                <path d='M9.4 12.4l1.9 1.9 3.6-4' />
              </>
            }
          >
            <VizLabel label={t('wins.stability.viz')} />
            <ul className='isp' data-isp>
              {LINKS.map((link, index) => (
                <li key={link} className='isp__i' data-isp-i={index}>
                  <span>{t(`wins.stability.links.${link}`)}</span>
                </li>
              ))}
            </ul>
            <p className='isp__msg' data-isp-msg>
              <i aria-hidden='true' />
              <span>{t('wins.stability.ok')}</span>
            </p>
            <p className='win__vf'>
              <span className='win__delta'>{t('wins.stability.foot_chip')}</span> {t('wins.stability.foot')}
            </p>
          </WinCard>
        </div>
      </div>
    </section>
  );
}
